// Currency data extracted from schema/index.ts to reduce file size
// 159 supported currencies organized by region

export const SUPPORTED_CURRENCIES = [
  // Israeli Shekel (default)
  "ILS",
  // Major currencies
  "EUR",
  "USD",
  "PLN",
  "GBP",
  // Global majors
  "JPY",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
  "HKD",
  "SGD",
  "SEK",
  "NOK",
  "DKK",
  "NZD",
  // Emerging markets
  "INR",
  "KRW",
  "MXN",
  "BRL",
  "ZAR",
  "TRY",
  "RUB",
  // Southeast Asia
  "THB",
  "MYR",
  "IDR",
  "PHP",
  "VND",
  "MMK",
  "KHR",
  "LAK",
  // Middle East & Gulf
  "AED",
  "SAR",
  "QAR",
  "KWD",
  "BHD",
  "OMR",
  "JOD",
  "EGP",
  "LBP",
  "IQD",
  // Eastern Europe
  "CZK",
  "HUF",
  "RON",
  "BGN",
  "HRK",
  "RSD",
  "UAH",
  "BYN",
  "MDL",
  "ALL",
  "MKD",
  "BAM",
  // Central Asia
  "GEL",
  "KZT",
  "UZS",
  "TJS",
  "TMT",
  // East Asia
  "MNT",
  "TWD",
  // South America
  "ARS",
  "CLP",
  "COP",
  "PEN",
  "UYU",
  "BOB",
  "PYG",
  "SRD",
  "GYD",
  // Central America & Caribbean
  "GTQ",
  "CRC",
  "PAB",
  "HNL",
  "NIO",
  "BZD",
  "SVC",
  "DOP",
  "JMD",
  "TTD",
  "BBD",
  "BSD",
  "XCD",
  "HTG",
  "AWG",
  "ANG",
  "KYD",
  // South Asia
  "PKR",
  "BDT",
  "LKR",
  "NPR",
  // Africa
  "NGN",
  "KES",
  "GHS",
  "ETB",
  "MAD",
  "TND",
  "DZD",
  "LYD",
  "SDG",
  "SSP",
  "AOA",
  "XOF",
  "XAF",
  "CDF",
  "UGX",
  "TZS",
  "RWF",
  "ZMW",
  "MWK",
  "BWP",
  "NAD",
  "SZL",
  "LSL",
  "MUR",
  "MZN",
  "GMD",
  "MRU",
  // Pacific
  "FJD",
  "PGK",
  "WST",
  "TOP",
  // Other
  "ISK",
] as const satisfies string[];

export type SupportedCurrencies = (typeof SUPPORTED_CURRENCIES)[number];

export const CURRENCY_SYMBOLS: Record<SupportedCurrencies, string> = {
  ILS: "₪",
  EUR: "€",
  USD: "$",
  GBP: "£",
  PLN: "zł",
  JPY: "¥",
  AUD: "$",
  CAD: "$",
  CHF: "Fr",
  CNY: "¥",
  HKD: "HK$",
  SGD: "S$",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  NZD: "NZ$",
  INR: "₹",
  KRW: "₩",
  MXN: "$",
  BRL: "R$",
  ZAR: "R",
  TRY: "₺",
  RUB: "₽",
  THB: "฿",
  MYR: "RM",
  IDR: "Rp",
  PHP: "₱",
  VND: "₫",
  MMK: "K",
  KHR: "៛",
  LAK: "₭",
  AED: "AED",
  SAR: "SAR",
  QAR: "QR",
  KWD: "KWD",
  BHD: "BHD",
  OMR: "OMR",
  JOD: "JOD",
  EGP: "EGP",
  LBP: "LBP",
  IQD: "IQD",
  CZK: "Kč",
  HUF: "Ft",
  RON: "lei",
  BGN: "лв",
  HRK: "kn",
  RSD: "дин",
  UAH: "₴",
  BYN: "Br",
  MDL: "L",
  ALL: "L",
  MKD: "ден",
  BAM: "KM",
  GEL: "₾",
  KZT: "₸",
  UZS: "so'm",
  TJS: "ЅМ",
  TMT: "m",
  MNT: "₮",
  TWD: "NT$",
  ARS: "$",
  CLP: "$",
  COP: "$",
  PEN: "S/",
  UYU: "$",
  BOB: "Bs",
  PYG: "₲",
  SRD: "$",
  GYD: "$",
  GTQ: "Q",
  CRC: "₡",
  PAB: "B/.",
  HNL: "L",
  NIO: "C$",
  BZD: "BZ$",
  SVC: "₡",
  DOP: "RD$",
  JMD: "J$",
  TTD: "TT$",
  BBD: "Bds$",
  BSD: "B$",
  XCD: "EC$",
  HTG: "G",
  AWG: "ƒ",
  ANG: "ƒ",
  KYD: "CI$",
  PKR: "₨",
  BDT: "৳",
  LKR: "Rs",
  NPR: "Rs",
  NGN: "₦",
  KES: "KSh",
  GHS: "₵",
  ETB: "Br",
  MAD: "MAD",
  TND: "TND",
  DZD: "دج",
  LYD: "LD",
  SDG: "SDG",
  SSP: "SS£",
  AOA: "Kz",
  XOF: "CFA",
  XAF: "FCFA",
  CDF: "FC",
  UGX: "USh",
  TZS: "TSh",
  RWF: "FRw",
  ZMW: "ZK",
  MWK: "MK",
  BWP: "P",
  NAD: "N$",
  SZL: "L",
  LSL: "L",
  MUR: "₨",
  MZN: "MT",
  GMD: "D",
  MRU: "UM",
  FJD: "FJ$",
  PGK: "K",
  WST: "WS$",
  TOP: "T$",
  ISK: "kr",
};

export type CurrencySymbols =
  (typeof CURRENCY_SYMBOLS)[keyof typeof CURRENCY_SYMBOLS];

export const CURRENCY_TO_LABEL: Record<SupportedCurrencies, string> = {
  ILS: "שקל חדש",
  EUR: "יורו",
  USD: "דולר אמריקאי",
  GBP: "לירה שטרלינג",
  PLN: "זלוטי פולני",
  JPY: "ין יפני",
  AUD: "דולר אוסטרלי",
  CAD: "דולר קנדי",
  CHF: "פרנק שוויצרי",
  CNY: "יואן סיני",
  HKD: "דולר הונג קונג",
  SGD: "דולר סינגפורי",
  SEK: "כתר שבדי",
  NOK: "כתר נורווגי",
  DKK: "כתר דני",
  NZD: "דולר ניו זילנדי",
  INR: "רופי הודי",
  KRW: "וון דרום קוריאני",
  MXN: "פזו מקסיקני",
  BRL: "ריאל ברזילאי",
  ZAR: "ראנד דרום אפריקאי",
  TRY: "לירה טורקית",
  RUB: "רובל רוסי",
  THB: "בהט תאילנדי",
  MYR: "רינגיט מלזי",
  IDR: "רופיה אינדונזית",
  PHP: "פזו פיליפיני",
  VND: "דונג וייטנאמי",
  MMK: "קיאט מיאנמר",
  KHR: "ריאל קמבודי",
  LAK: "קיפ לאוסי",
  AED: "דירהם איחוד האמירויות",
  SAR: "ריאל סעודי",
  QAR: "ריאל קטרי",
  KWD: "דינר כוויתי",
  BHD: "דינר בחרייני",
  OMR: "ריאל עומאני",
  JOD: "דינר ירדני",
  EGP: "לירה מצרית",
  LBP: "לירה לבנונית",
  IQD: "דינר עיראקי",
  CZK: "קורונה צ'כית",
  HUF: "פורינט הונגרי",
  RON: "לאו רומני",
  BGN: "לב בולגרי",
  HRK: "קונה קרואטית",
  RSD: "דינר סרבי",
  UAH: "גריבנה אוקראינית",
  BYN: "רובל בלארוסי",
  MDL: "לאו מולדובני",
  ALL: "לק אלבני",
  MKD: "דינר מקדוני",
  BAM: "מארק בר המרה בוסני",
  GEL: "לארי גאורגי",
  KZT: "טנגה קזחית",
  UZS: "סום אוזבקי",
  TJS: "סומוני טג'יקי",
  TMT: "מאנאט טורקמני",
  MNT: "טוגריק מונגולי",
  TWD: "דולר טייוואני",
  ARS: "פזו ארגנטינאי",
  CLP: "פזו צ'יליאני",
  COP: "פזו קולומביאני",
  PEN: "סול פרואני",
  UYU: "פזו אורוגוואי",
  BOB: "בוליביאנו בוליביאני",
  PYG: "גוארני פרגוואי",
  SRD: "דולר סורינאמי",
  GYD: "דולר גיאני",
  GTQ: "קצל גואטמלי",
  CRC: "קולון קוסטה ריקני",
  PAB: "בלבואה פנמי",
  HNL: "למפירה הונדורסית",
  NIO: "קורדובה ניקרגואה",
  BZD: "דולר בליזי",
  SVC: "קולון אל סלבדורי",
  DOP: "פזו דומיניקני",
  JMD: "דולר ג'מייקני",
  TTD: "דולר טרינידד וטובגו",
  BBD: "דולר ברבדי",
  BSD: "דולר בהאמי",
  XCD: "דולר מזרח קריבי",
  HTG: "גורד האיטי",
  AWG: "פלורין ארובה",
  ANG: "גילדר אנטילים הולנדיים",
  KYD: "דולר איי קיימן",
  PKR: "רופי פקיסטני",
  BDT: "טאקה בנגלדשי",
  LKR: "רופי סרי לנקי",
  NPR: "רופי נפאלי",
  NGN: "נאירה ניגרי",
  KES: "שילינג קנייתי",
  GHS: "סדי גאני",
  ETB: "ביר אתיופי",
  MAD: "דירהם מרוקאי",
  TND: "דינר טוניסאי",
  DZD: "דינר אלג'יראי",
  LYD: "דינר לובי",
  SDG: "לירה סודנית",
  SSP: "לירה דרום סודנית",
  AOA: "קואנזה אנגולי",
  XOF: "פרנק CFA מערב אפריקה",
  XAF: "פרנק CFA מרכז אפריקה",
  CDF: "פרנק קונגולזי",
  UGX: "שילינג אוגנדי",
  TZS: "שילינג טנזני",
  RWF: "פרנק רואנדי",
  ZMW: "קוואצ'ה זמביאני",
  MWK: "קוואצ'ה מלאווי",
  BWP: "פולה בוצואני",
  NAD: "דולר נמיבי",
  SZL: "לילנגני סווזי",
  LSL: "לוטי לסוטו",
  MUR: "רופי מאוריצי",
  MZN: "מטיקל מוזמביקי",
  GMD: "דלסי גמבי",
  MRU: "אוגיאה מאוריטני",
  FJD: "דולר פיג'י",
  PGK: "קינה פפואה ניו גיני",
  WST: "טאלה סמואי",
  TOP: "פאנגה טונגי",
  ISK: "כתר איסלנדי",
};

interface CurrencyGroup {
  label: string;
  currencies: SupportedCurrencies[];
}

export const CURRENCY_GROUPS: CurrencyGroup[] = [
  { label: "שקל ישראלי", currencies: ["ILS"] },
  { label: "פופולריות", currencies: ["EUR", "USD", "PLN", "GBP"] },
  {
    label: "עיקריות עולמיות",
    currencies: [
      "JPY",
      "AUD",
      "CAD",
      "CHF",
      "CNY",
      "HKD",
      "SGD",
      "SEK",
      "NOK",
      "DKK",
      "NZD",
    ],
  },
  {
    label: "אירופה",
    currencies: [
      "CZK",
      "HUF",
      "RON",
      "BGN",
      "HRK",
      "RSD",
      "UAH",
      "BYN",
      "MDL",
      "ALL",
      "MKD",
      "BAM",
      "ISK",
      "RUB",
      "TRY",
    ],
  },
  {
    label: "אסיה ופסיפיק",
    currencies: [
      "INR",
      "KRW",
      "THB",
      "MYR",
      "IDR",
      "PHP",
      "VND",
      "MMK",
      "KHR",
      "LAK",
      "PKR",
      "BDT",
      "LKR",
      "NPR",
      "MNT",
      "TWD",
      "GEL",
      "KZT",
      "UZS",
      "TJS",
      "TMT",
      "FJD",
      "PGK",
      "WST",
      "TOP",
    ],
  },
  {
    label: "מזרח תיכון ומפרץ",
    currencies: [
      "AED",
      "SAR",
      "QAR",
      "KWD",
      "BHD",
      "OMR",
      "JOD",
      "EGP",
      "LBP",
      "IQD",
    ],
  },
  {
    label: "אמריקה הלטינית והקריביים",
    currencies: [
      "MXN",
      "BRL",
      "ARS",
      "CLP",
      "COP",
      "PEN",
      "UYU",
      "BOB",
      "PYG",
      "SRD",
      "GYD",
      "GTQ",
      "CRC",
      "PAB",
      "HNL",
      "NIO",
      "BZD",
      "SVC",
      "DOP",
      "JMD",
      "TTD",
      "BBD",
      "BSD",
      "XCD",
      "HTG",
      "AWG",
      "ANG",
      "KYD",
    ],
  },
  {
    label: "אפריקה",
    currencies: [
      "ZAR",
      "NGN",
      "KES",
      "GHS",
      "ETB",
      "MAD",
      "TND",
      "DZD",
      "LYD",
      "SDG",
      "SSP",
      "AOA",
      "XOF",
      "XAF",
      "CDF",
      "UGX",
      "TZS",
      "RWF",
      "ZMW",
      "MWK",
      "BWP",
      "NAD",
      "SZL",
      "LSL",
      "MUR",
      "MZN",
      "GMD",
      "MRU",
    ],
  },
];

export interface CurrencyComboboxItem {
  code: SupportedCurrencies;
  searchLabel: string;
}

export interface CurrencyComboboxGroup {
  value: string;
  items: CurrencyComboboxItem[];
}

export const CURRENCY_COMBOBOX_GROUPS: CurrencyComboboxGroup[] =
  CURRENCY_GROUPS.map((group) => ({
    value: group.label,
    items: group.currencies.map((code) => ({
      code,
      searchLabel: `${code} ${CURRENCY_SYMBOLS[code]} ${CURRENCY_TO_LABEL[code]}`,
    })),
  }));

// Validation: Check currency uniqueness
const uniqueCurrencies = new Set(SUPPORTED_CURRENCIES);
if (uniqueCurrencies.size !== SUPPORTED_CURRENCIES.length) {
  const duplicates = SUPPORTED_CURRENCIES.filter(
    (currency, index) => SUPPORTED_CURRENCIES.indexOf(currency) !== index,
  );
  throw new Error(`Duplicate currencies: ${duplicates.join(", ")}`);
}

const currenciesInGroups = CURRENCY_GROUPS.flatMap((g) => g.currencies);
const currenciesInGroupsSet = new Set(currenciesInGroups);
if (currenciesInGroupsSet.size !== currenciesInGroups.length) {
  const duplicates = currenciesInGroups.filter(
    (currency, index) => currenciesInGroups.indexOf(currency) !== index,
  );
  throw new Error(`Duplicate currencies in groups: ${duplicates.join(", ")}`);
}

const missingCurrencies = SUPPORTED_CURRENCIES.filter(
  (c) => !currenciesInGroupsSet.has(c),
);
if (missingCurrencies.length > 0) {
  throw new Error(
    `Missing currencies in groups: ${missingCurrencies.join(", ")}`,
  );
}
